import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

export function LoginSuccessOverlay({
  show,
  onDone,
}: {
  show: boolean;
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<"hidden" | "enter" | "hold" | "exit">("hidden");

  useEffect(() => {
    if (!show) return;
    setPhase("enter");
    const t1 = setTimeout(() => setPhase("hold"), 600);
    const t2 = setTimeout(() => setPhase("exit"), 2200);
    const t3 = setTimeout(() => {
      setPhase("hidden");
      onDone();
    }, 2900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [show, onDone]);

  if (phase === "hidden") return null;

  const isEnter = phase === "enter";
  const isExit = phase === "exit";

  const content = (
    <div
      className={`
        fixed inset-0 z-[100] flex items-center justify-center
        transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
        ${isExit ? "opacity-0" : "opacity-100"}
      `}
      style={{ pointerEvents: isExit ? "none" : "auto" }}
    >
      {/* Backdrop */}
      <div
        className={`
          absolute inset-0 bg-black/50 backdrop-blur-md
          transition-opacity duration-500
          ${isEnter ? "opacity-0" : "opacity-100"}
          ${isExit ? "opacity-0" : ""}
        `}
      />

      {/* Content wrapper – centered by flexbox, never moves */}
      <div className="relative flex flex-col items-center gap-5">
        {/* Animated inner content */}
        <div
          className={`
            flex flex-col items-center gap-5
            transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)]
            ${isEnter ? "scale-[0.3] opacity-0 translate-y-8" : ""}
            ${isExit ? "scale-110 opacity-0 -translate-y-4" : ""}
            ${phase === "hold" ? "scale-100 opacity-100 translate-y-0" : ""}
          `}
        >
          {/* Animated rings */}
          <div className="relative flex items-center justify-center">
            {/* Outer pulsing ring */}
            <div
              className={`
                absolute rounded-full border-2 border-primary/40
                transition-all duration-1000
                ${isEnter ? "h-0 w-0 opacity-0" : "h-32 w-32 opacity-100"}
                ${phase === "hold" ? "animate-[ping_2s_ease-out_infinite]" : ""}
              `}
              style={{ animationDelay: "0.3s" }}
            />
            {/* Middle ring */}
            <div
              className={`
                absolute rounded-full border border-primary/30
                transition-all duration-700 delay-100
                ${isEnter ? "h-0 w-0 opacity-0" : "h-24 w-24 opacity-100"}
                ${phase === "hold" ? "animate-[ping_2.5s_ease-out_infinite]" : ""}
              `}
              style={{ animationDelay: "0.5s" }}
            />
            {/* Main check circle */}
            <div
              className={`
                relative flex h-20 w-20 items-center justify-center rounded-full
                bg-primary
                transition-all duration-500 delay-150
                ${isEnter ? "scale-0 rotate-[-180deg]" : "scale-100 rotate-0"}
              `}
              style={{
                boxShadow: "0 0 40px color-mix(in oklab, var(--primary) 40%, transparent)",
              }}
            >
              <Check
                className={`
                  h-10 w-10 text-primary-foreground
                  transition-all duration-400 delay-300
                  ${isEnter ? "scale-0 opacity-0" : "scale-100 opacity-100"}
                `}
                strokeWidth={3}
              />
            </div>
          </div>

          {/* Text with staggered reveal */}
          <div className="flex flex-col items-center gap-1">
            <span
              className={`
                text-2xl font-bold text-white
                transition-all duration-500 delay-200
                ${isEnter ? "translate-y-4 opacity-0" : "translate-y-0 opacity-100"}
                ${isExit ? "translate-y-[-8px] opacity-0" : ""}
              `}
            >
              Estás logueado
            </span>
            <span
              className={`
                text-sm text-white/70
                transition-all duration-500 delay-300
                ${isEnter ? "translate-y-4 opacity-0" : "translate-y-0 opacity-100"}
                ${isExit ? "opacity-0" : ""}
              `}
            >
              Bienvenido de nuevo
            </span>
          </div>
        </div>

        {/* Decorative dots */}
        <div className="absolute inset-0 pointer-events-none overflow-visible">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`
                absolute h-2 w-2 rounded-full bg-primary/60
                transition-all duration-700
                ${isEnter ? "scale-0 opacity-0" : "scale-100 opacity-100"}
              `}
              style={{
                top: `${20 + Math.sin((i * Math.PI) / 3) * 60}%`,
                left: `${50 + Math.cos((i * Math.PI) / 3) * 50}%`,
                transitionDelay: `${350 + i * 80}ms`,
                animation: phase === "hold" ? `float 3s ease-in-out ${i * 0.4}s infinite` : "none",
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.6; }
          50% { transform: translateY(-10px) scale(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );

  return createPortal(content, document.body);
}
